"use client";

import { Info } from "lucide-react";
import { useTranslations } from "next-intl";
import { useAccount } from "wagmi";
import { WithdrawDnmCard } from "@/components/features/dao/election/WithdrawDnmCard";
import { useCurrentCycle } from "@/hooks/dao/useCurrentCycle";
import { useLockedDnmStatus } from "@/hooks/dao/voting/useLockedDnmStatus";
import { useWithdrawDnm } from "@/hooks/dao/voting/useWithdrawDnm";

/**
 * Section rendered above CandidatesList — Figma 5462:51619 (Phase 2).
 *
 * Reuses existing WithdrawDnmCard with the three state variants it already implements.
 * Hidden when variant === 'hidden' or while loading.
 *
 * Below the status card, an optional guide paragraph explains DNM voting
 * mechanics. Visibility is driven by the `showGuide` prop, toggled from a
 * "راهنما" button next to the BeCandidateButton in ElectionTab.
 *
 * AC-24 (deposited), AC-25 (active/withdraw), AC-26 (withdrawn), AC-27 (hidden),
 * AC-28 (card body text reused), AC-29 (withdraw button), AC-30 (invalidation),
 * AC-31 (error toasts — inside useWithdrawDnm).
 */

interface LockedDnmStatusSectionProps {
  showGuide?: boolean;
}

export const LockedDnmStatusSection: React.FC<LockedDnmStatusSectionProps> = ({
  showGuide = false,
}) => {
  const t = useTranslations("dao");
  const { address: userAddress } = useAccount();
  const { cycle } = useCurrentCycle();
  const { variant, isLoading } = useLockedDnmStatus(userAddress, cycle);
  const withdraw = useWithdrawDnm();

  // Guide info text REPLACES the WithdrawDnmCard when toggled on — same slot,
  // mutually exclusive content. When guide is off and the user has no locked
  // DNM (variant === "hidden"), the whole section collapses to render null.
  if (showGuide) {
    return (
      <section className="mb-4">
        <p className="flex items-start gap-2 text-xs leading-[1.6] text-text-secondary px-1">
          <Info className="size-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span className="flex-1 text-start">
            {t("voting.lockedStatus.guide")}
          </span>
        </p>
      </section>
    );
  }

  if (variant === "hidden" || isLoading) {
    return null;
  }

  return (
    <section className="mb-4">
      {/* Reuse existing WithdrawDnmCard — preserves Phase 1 body text + chips verbatim */}
      <WithdrawDnmCard
        state={variant}
        onWithdraw={() => withdraw.mutate()}
        isPending={withdraw.isPending}
      />
    </section>
  );
};
